import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { supabase } from '../lib/supabase';
import { track } from '../utils/analytics';
import { computeUserTier } from '../lib/trialCheck';
import {
  checkAccountRateLimit,
  recordFailedAttempt,
  resetAccountRateLimit,
  ACCOUNT_MAX_ATTEMPTS,
} from '../lib/rateLimit';
import { validatePassword } from '../lib/passwordValidation';

const OAUTH_REDIRECT = `${window.location.origin}/auth/callback`;

type Tab = 'signin' | 'signup' | 'forgot';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || '/app';
  const queryTab = new URLSearchParams(location.search).get('tab');
  const stateTab = (location.state as { tab?: string })?.tab;
  const initialTab = (queryTab === 'signup' || stateTab === 'signup') ? 'signup' : 'signin';

  const [tab, setTab] = useState<Tab>(initialTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const reset = (nextTab: Tab) => {
    setTab(nextTab);
    setError('');
    setMessage('');
    setPassword('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    if (tab === 'signup') {
      const validation = validatePassword(password);
      if (!validation.valid) {
        setError(validation.error ?? 'Invalid password');
        setLoading(false);
        return;
      }
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) {
        setError(error.message);
      } else {
        track('signup_completed');
        setMessage('Account created! Check your email to confirm, then sign in.');
        reset('signin');
      }

    } else if (tab === 'signin') {
      // Check account rate limit before attempting authentication
      const accountLimit = checkAccountRateLimit(email, ACCOUNT_MAX_ATTEMPTS);
      if (!accountLimit.allowed) {
        const retryMinutes = Math.ceil(accountLimit.retryAfterMs / 60000);
        setError(`Too many login attempts for this account. Please try again in ${retryMinutes} minute${retryMinutes !== 1 ? 's' : ''}.`);
        setLoading(false);
        return;
      }

      const { data: signinData, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        recordFailedAttempt(email);
        setError('Invalid email or password.');
      } else {
        const userId = signinData.user?.id;
        // Check if user has subscribed (not blocked by paywall)
        const { data: profile } = userId
          ? await supabase.from('profiles').select('subscribed, trial_expires_at').eq('id', userId).single()
          : { data: null };
        if (profile) {
          const tier = computeUserTier(profile);
          resetAccountRateLimit(email); // Reset on successful auth
          if (tier === 'subscribed' || tier === 'trial-active') {
            navigate('/app', { replace: true });
          } else {
            navigate('/pricing?trial_expired=true', { replace: true });
          }
        } else {
          navigate('/pricing?welcome=true', { replace: true });
        }
      }

    } else if (tab === 'forgot') {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      });
      if (error) {
        setError(error.message);
      } else {
        setMessage('Password reset link sent — check your email.');
      }
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--color-csai-bg)] px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="font-serif font-bold text-3xl text-[var(--color-csai-primary)]">Situs Canada</Link>
        </div>

        <Card className="p-8 sm:p-10 shadow-xl border border-[var(--color-csai-surface)]">
          {/* Tabs */}
          {tab !== 'forgot' && (
            <div className="flex mb-8 border-b border-[var(--color-csai-surface)]">
              <button
                className={`flex-1 pb-3 text-sm font-semibold transition-colors ${tab === 'signin' ? 'text-[var(--color-csai-primary)] border-b-2 border-[var(--color-csai-primary)]' : 'text-[var(--color-csai-muted)]'}`}
                onClick={() => reset('signin')}
              >
                Sign In
              </button>
              <button
                className={`flex-1 pb-3 text-sm font-semibold transition-colors ${tab === 'signup' ? 'text-[var(--color-csai-primary)] border-b-2 border-[var(--color-csai-primary)]' : 'text-[var(--color-csai-muted)]'}`}
                onClick={() => reset('signup')}
              >
                Create Account
              </button>
            </div>
          )}

          {tab === 'forgot' && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-[var(--color-csai-accent)]">Reset Password</h2>
              <p className="text-sm text-[var(--color-csai-muted)] mt-1">We'll send a reset link to your email.</p>
            </div>
          )}

          {tab !== 'forgot' && (
            <div className="space-y-3 mb-6">
              <button
                type="button"
                onClick={() => supabase.auth.signInWithOAuth({
                  provider: 'google',
                  options: {
                    redirectTo: OAUTH_REDIRECT,
                    queryParams: {
                      prompt: 'select_account',
                      access_type: 'offline'
                    }
                  }
                })}
                className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-[var(--color-csai-surface)] rounded-lg text-sm font-medium text-[var(--color-csai-accent)] bg-white hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Continue with Google
              </button>
              <div className="relative flex items-center">
                <div className="flex-grow border-t border-gray-200" />
                <span className="mx-3 text-xs text-[var(--color-csai-muted)]">or continue with email</span>
                <div className="flex-grow border-t border-gray-200" />
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[var(--color-csai-accent)] mb-2" htmlFor="email">
                Email address
              </label>
              <Input
                id="email"
                type="email"
                required
                placeholder="accountant@firm.ca"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full"
              />
            </div>

            {tab !== 'forgot' && (
              <div>
                <label className="block text-sm font-medium text-[var(--color-csai-accent)] mb-2" htmlFor="password">
                  Password {tab === 'signup' && <span className="text-[var(--color-csai-muted)] font-normal">(min 8 chars)</span>}
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-csai-muted)] hover:text-[var(--color-csai-accent)]"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {tab === 'signin' && (
                  <div className="mt-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => reset('forgot')}
                      className="text-xs text-[var(--color-csai-muted)] hover:text-[var(--color-csai-primary)]"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}
            {message && <p className="text-sm text-emerald-600">{message}</p>}

            <Button type="submit" className="w-full h-12 text-base" disabled={loading}>
              {loading ? 'Please wait...' : tab === 'signin' ? 'Sign In' : tab === 'signup' ? 'Create Account' : 'Send Reset Link'}
            </Button>

            {tab === 'forgot' && (
              <button
                type="button"
                onClick={() => reset('signin')}
                className="w-full text-sm text-[var(--color-csai-muted)] hover:text-[var(--color-csai-primary)] text-center"
              >
                Back to Sign In
              </button>
            )}
          </form>
        </Card>

        <div className="mt-8 text-center">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)]">
            <span className="mr-1">🇨🇦</span> Data processed in Canada
          </span>
        </div>
      </div>
    </div>
  );
}
